import { queryOptions } from '@tanstack/react-query'
import { type AuditListQuery, type Role, reads, type SnippetAudience } from './api'

export const currentUserQuery = () =>
  queryOptions({
    queryKey: ['me'],
    queryFn: () => reads().me(),
  })

export const peopleQuery = () =>
  queryOptions({
    queryKey: ['admin', 'people'],
    queryFn: () => reads().people(),
  })

export const projectsImportQuery = () =>
  queryOptions({
    queryKey: ['admin', 'projects-import'],
    queryFn: () => reads().projectsImport(),
  })

export const removedCasesQuery = () =>
  queryOptions({
    queryKey: ['admin', 'removed-cases'],
    queryFn: () => reads().removedCases(),
  })

export const settingsQuery = () =>
  queryOptions({
    queryKey: ['admin', 'settings'],
    queryFn: () => reads().settings(),
  })

export const featureFlagsQuery = () =>
  queryOptions({
    queryKey: ['admin', 'feature-flags'],
    queryFn: () => reads().featureFlags(),
  })

export const automationQuery = () =>
  queryOptions({
    queryKey: ['admin', 'automation'],
    queryFn: () => reads().automation(),
  })

export const auditQuery = (filters: AuditListQuery = {}) =>
  queryOptions({
    queryKey: ['admin', 'audit', filters.actor ?? '', filters.actionGroup ?? '', filters.caseRef ?? ''] as const,
    queryFn: () =>
      reads().audit({
        ...(filters.actor ? { actor: filters.actor } : {}),
        ...(filters.actionGroup ? { actionGroup: filters.actionGroup } : {}),
        ...(filters.caseRef ? { caseRef: filters.caseRef } : {}),
      }),
  })

export const integrationsQuery = () =>
  queryOptions({
    queryKey: ['admin', 'integrations'],
    queryFn: async () => {
      // Liveness carries the outbox backlog and SSE client counts. It is read on its own, so a failed liveness read
      // shows those counts as unknown instead of failing the whole screen (and never as zero).
      const [health, budgets, quota, liveness, config] = await Promise.all([
        reads().integrations(),
        reads().connectorBudgets(),
        reads().zohoQuota(),
        reads()
          .liveness()
          .then((value) => ({ ok: true as const, value }))
          .catch((error: unknown) => ({ ok: false as const, error: error instanceof Error ? error.message : 'unreachable' })),
        // Which env-configured integrations are set up (#294). Read on its own: a failure is "unknown", never "configured".
        reads()
          .integrationConfig()
          .then((value) => ({ ok: true as const, value }))
          .catch((error: unknown) => ({ ok: false as const, error: error instanceof Error ? error.message : 'unreachable' })),
      ])
      return { health, budgets, quota, liveness, config }
    },
  })

export const techRatesQuery = () =>
  queryOptions({
    queryKey: ['rates', 'tech'],
    queryFn: () => reads().techRates(),
  })

export const rileyRateQuery = () =>
  queryOptions({
    queryKey: ['rates', 'riley'],
    queryFn: () => reads().rileyRate(),
  })

export const contractorRatesQuery = (contractorId: string | null) =>
  queryOptions({
    queryKey: ['rates', 'contractor', contractorId],
    queryFn: () => {
      if (!contractorId) return Promise.resolve([])
      return reads().contractorRates(contractorId)
    },
    enabled: Boolean(contractorId),
  })

export const contractorSearchQuery = (query: string) =>
  queryOptions({
    queryKey: ['search', 'contractor', query],
    queryFn: async () => {
      const rows = await reads().search(query)
      return rows.filter((row) => row.kind === 'contractor')
    },
    enabled: query.trim().length >= 2,
  })

export const rileyTriageQuery = (deskTicketId: string) =>
  queryOptions({
    queryKey: ['admin', 'riley', 'triage', deskTicketId],
    queryFn: () => reads().rileyTriage(deskTicketId),
    enabled: /^\d{1,20}$/.test(deskTicketId),
  })

/** One org dossier from Riley (GET /ops/org via Patchbay): the whole body, read-only, plus its `## Handling` section. */
export const rileyOrgQuery = (org: string) =>
  queryOptions({
    queryKey: ['admin', 'riley', 'org', org],
    queryFn: () => reads().rileyOrg(org),
    enabled: /^[A-Za-z0-9._@+-]{1,254}$/.test(org),
    retry: false,
  })

/** Attached hub agent identities (MCP keys; never the keys themselves) with their per-connector liveness. */
export const hubIdentitiesQuery = () =>
  queryOptions({
    queryKey: ['admin', 'hub', 'identities'],
    queryFn: async () => {
      const [identities, liveness] = await Promise.all([reads().hubIdentities(), reads().hubLiveness()])
      return { identities, liveness }
    },
  })

export const hubCallsQuery = (identity: string | null) =>
  queryOptions({
    queryKey: ['admin', 'hub', 'calls', identity ?? 'all'],
    queryFn: () => reads().hubCalls(identity ?? undefined),
  })

export const adminSnippetsQuery = () =>
  queryOptions({
    queryKey: ['admin', 'snippets'],
    queryFn: () => reads().adminSnippets(true),
  })

export const snippetsQuery = (audience?: SnippetAudience) =>
  queryOptions({
    queryKey: ['snippets', audience ?? 'all'],
    queryFn: () => reads().snippets(audience),
  })

export function canEnterAdmin(role: Role, pathname: string, isSuperadmin = false): boolean {
  if (pathname.startsWith('/admin/removed')) return role === 'manager' && isSuperadmin
  if (role === 'manager') return true
  if (role === 'cover') return pathname.startsWith('/admin/contractors')
  return role === 'billing' && pathname.startsWith('/admin/rates')
}

/** Env-configured integrations (manager-only). Knowledge uses it to explain a disabled Approve (#294). */
export const integrationConfigQuery = () =>
  queryOptions({
    queryKey: ['admin', 'integrations', 'configuration'],
    queryFn: () => reads().integrationConfig(),
  })
